import { Marker, Popup, Tooltip, Circle } from 'react-leaflet'
import L from 'leaflet'
import { memo, Fragment } from 'react'
import type { LegAssignment, LegLog, Runner } from '../../types/index'
import { formatDuration } from '../../utils/formatTime'
import { formatPace } from '../../utils/paceCalc'

interface ExchangePointMarkersProps {
  legs: LegAssignment[]
  logs?: LegLog[]
  runners?: Runner[]
  currentLegNumber: number
}

function xpIcon(label: string | number, state: 'past' | 'current' | 'upcoming') {
  const s = String(label)
  const bg = state === 'past' ? '#94a3b8' : state === 'current' ? '#22c55e' : '#475569'
  const border = state === 'current' ? '2px solid #15803d' : '1.5px solid white'
  const fontSize = s.length > 2 ? '8px' : s.length > 1 ? '10px' : '11px'
  const html = `<div style="background:${bg};color:white;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:${fontSize};font-weight:700;border:${border};box-shadow:0 1px 5px rgba(0,0,0,0.45);letter-spacing:-0.5px">${s}</div>`
  return L.divIcon({ html, className: '', iconSize: [26, 26], iconAnchor: [13, 13] })
}

function starIcon(isStar: boolean) {
  const html = `<div style="background:#1d4ed8;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:13px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.5)">${isStar ? '★' : '🏁'}</div>`
  return L.divIcon({ html, className: '', iconSize: [24, 24], iconAnchor: [12, 12] })
}

const PT = { timeZone: 'America/Los_Angeles' }
function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-US', { ...PT, hour: '2-digit', minute: '2-digit' })
}

// 600 feet in meters — matches the proximity gate in MapPage
const SIX_HUNDRED_FEET_METERS = 182.88

const NavLink = ({ lat, lng }: { lat: number; lng: number }) => (
  <a
    href={`https://maps.google.com/maps?daddr=${lat},${lng}`}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-1 text-blue-600 hover:underline"
    style={{ fontSize: '11px', marginTop: '4px' }}
  >
    <svg style={{ width: 10, height: 10, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
    Navigate here
  </a>
)

export const ExchangePointMarkers = memo(function ExchangePointMarkers({
  legs, logs = [], runners = [], currentLegNumber
}: ExchangePointMarkersProps) {
  if (legs.length === 0) return null

  const sortedLegs = [...legs].sort((a, b) => a.legNumber - b.legNumber)
  const leg1 = sortedLegs[0]
  const lastLeg = sortedLegs[sortedLegs.length - 1]

  // Lookup maps
  const logByLegNum = new Map(logs.map(l => [l.legNumber, l]))
  const runnerById = new Map(runners.map(r => [r.id, r]))

  // Compute expected chain start times from leg 1's actual start
  const leg1Log = logByLegNum.get(1)
  const chainStartTimes = new Map<number, number>() // legNumber → ms timestamp
  if (leg1Log?.startTime) {
    let t = new Date(leg1Log.startTime).getTime()
    for (const leg of sortedLegs) {
      chainStartTimes.set(leg.legNumber, t)
      const runner = leg.runnerId ? runnerById.get(leg.runnerId) : null
      const pace = leg.estimatedPacePerMile ?? runner?.expectedPace ?? 10
      const durationMs = (leg.distanceMiles ?? 0) * pace * 60 * 1000
      t += durationMs
    }
  }

  const renderRunnerSection = (leg: LegAssignment, log: LegLog | undefined) => {
    const runner = leg.runnerId ? runnerById.get(leg.runnerId) : null
    const pace = leg.estimatedPacePerMile ?? runner?.expectedPace ?? 10
    const estDurationSec = leg.distanceMiles ? Math.floor(leg.distanceMiles * pace * 60) : null
    const estStart = chainStartTimes.get(leg.legNumber)
    const estEnd = estStart && estDurationSec ? estStart + estDurationSec * 1000 : null
    const isCompleted = !!(log?.endTime)

    return (
      <>
        <div style={{ fontSize: 11, color: '#374151', marginBottom: 3 }}>
          {runner ? `${runner.name} · Van ${runner.vanNumber}` : 'Unassigned'}
          {isCompleted && <span style={{ marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>✓</span>}
        </div>
        {isCompleted && log ? (
          <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px', color: '#374151' }}>
            <span style={{ color: '#6b7280' }}>Start</span><span style={{ fontWeight: 600 }}>{fmtTime(log.startTime)}</span>
            <span style={{ color: '#6b7280' }}>End</span><span style={{ fontWeight: 600 }}>{fmtTime(log.endTime)}</span>
            <span style={{ color: '#6b7280' }}>Time</span><span style={{ fontWeight: 600 }}>{log.actualDurationSeconds ? formatDuration(log.actualDurationSeconds) : '—'}</span>
            <span style={{ color: '#6b7280' }}>Pace</span><span style={{ fontWeight: 600 }}>{log.actualPacePerMile ? formatPace(log.actualPacePerMile) : '—'}</span>
          </div>
        ) : (
          <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px', color: '#374151' }}>
            {estStart && <><span style={{ color: '#6b7280' }}>Est start</span><span style={{ fontWeight: 600 }}>{fmtTime(new Date(estStart).toISOString())}</span></>}
            {estEnd && <><span style={{ color: '#6b7280' }}>Est end</span><span style={{ fontWeight: 600 }}>{fmtTime(new Date(estEnd).toISOString())}</span></>}
            {estDurationSec && <><span style={{ color: '#6b7280' }}>Est time</span><span style={{ fontWeight: 600 }}>{formatDuration(estDurationSec)}</span></>}
            <span style={{ color: '#6b7280' }}>Pace</span><span style={{ fontWeight: 600 }}>{formatPace(pace)}</span>
          </div>
        )}
      </>
    )
  }

  const renderLegPopup = (leg: LegAssignment, lat: number, lng: number, label: string, nextLeg?: LegAssignment, isVanTransferEnd = false, isVanTransferStart = false) => {
    const log = logByLegNum.get(leg.legNumber)
    const nextLog = nextLeg ? logByLegNum.get(nextLeg.legNumber) : undefined
    const xpInstr = isVanTransferStart
      ? (nextLeg?.xpInstructions ?? null)
      : (nextLeg?.xpInstructions ?? leg.xpInstructions ?? null)

    return (
      <Popup minWidth={200}>
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{label}</div>

        {/* Van transfer banner */}
        {(isVanTransferEnd || isVanTransferStart) && (
          <div style={{ background: '#ede9fe', border: '1px solid #a78bfa', borderRadius: 4, padding: '3px 6px', fontSize: 10, color: '#5b21b6', marginBottom: 6 }}>
            🚐 {isVanTransferEnd ? 'Runners load vans here' : 'Van transfer arrival — runners start here'}
          </div>
        )}

        {/* XP instructions banner */}
        {xpInstr && (
          <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4, padding: '3px 6px', fontSize: 10, color: '#92400e', marginBottom: 6 }}>
            ℹ️ {xpInstr}
          </div>
        )}

        {/* Finishing runner — hidden on Nb (runners haven't run to this point) */}
        {!isVanTransferStart && (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
              Leg {leg.legNumber} · Finishing
            </div>
            {renderRunnerSection(leg, log)}
          </>
        )}

        {/* Starting runner */}
        {nextLeg && (
          <>
            {!isVanTransferStart && <div style={{ borderTop: '1px solid #e5e7eb', margin: '6px 0' }} />}
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
              Leg {nextLeg.legNumber} · Starting
            </div>
            {renderRunnerSection(nextLeg, nextLog)}
          </>
        )}

        <NavLink lat={lat} lng={lng} />
      </Popup>
    )
  }

  return (
    <>
      {/* Start/Finish at start of Leg 1 */}
      {leg1 && (
        <Marker position={[leg1.startLat, leg1.startLng]} icon={starIcon(true)} zIndexOffset={1000}>
          <Tooltip direction="top" offset={[0, -14]} opacity={0.9}>
            <span className="text-xs font-semibold">Start / Finish</span>
          </Tooltip>
          {renderLegPopup(leg1, leg1.startLat, leg1.startLng, '★ Start / Finish')}
        </Marker>
      )}

      {/* XP 1–35 at end of each leg + proximity circle */}
      {sortedLegs
        .filter(leg => leg.legNumber <= 35)
        .map(leg => {
          const nextLeg = sortedLegs.find(l => l.legNumber === leg.legNumber + 1)
          const isVanTransfer = !!leg.vanTransfer
          const state = leg.legNumber < currentLegNumber ? 'past'
            : leg.legNumber === currentLegNumber ? 'current'
            : 'upcoming'
          const circleColor = state === 'past' ? '#94a3b8' : state === 'current' ? '#22c55e' : '#64748b'
          const endLabel = isVanTransfer ? `${leg.legNumber}a` : leg.legNumber
          return (
            <Fragment key={`xp-${leg.legNumber}`}>
              {/* XP end marker (XP Na for van transfers, XP N otherwise) */}
              <Circle
                center={[leg.endLat, leg.endLng]}
                radius={SIX_HUNDRED_FEET_METERS}
                color={circleColor}
                fillColor={circleColor}
                fillOpacity={0.08}
                weight={1.5}
                dashArray="5, 5"
              />
              <Marker
                position={[leg.endLat, leg.endLng]}
                icon={xpIcon(endLabel, state)}
                zIndexOffset={state === 'current' ? 800 : 400}
              >
                <Tooltip direction="top" offset={[0, -15]} opacity={0.9}>
                  <span className="text-xs font-semibold">XP {endLabel}</span>
                </Tooltip>
                {renderLegPopup(leg, leg.endLat, leg.endLng,
                  isVanTransfer ? `XP ${leg.legNumber}a — Van Transfer` : `XP ${leg.legNumber}`,
                  isVanTransfer ? undefined : nextLeg,
                  isVanTransfer
                )}
              </Marker>

              {/* XP Nb marker — start of next leg after a van transfer */}
              {isVanTransfer && nextLeg && (
                <Fragment key={`xp-${leg.legNumber}b`}>
                  <Circle
                    center={[nextLeg.startLat, nextLeg.startLng]}
                    radius={SIX_HUNDRED_FEET_METERS}
                    color={circleColor}
                    fillColor={circleColor}
                    fillOpacity={0.08}
                    weight={1.5}
                    dashArray="5, 5"
                  />
                  <Marker
                    position={[nextLeg.startLat, nextLeg.startLng]}
                    icon={xpIcon(`${leg.legNumber}b`, state)}
                    zIndexOffset={state === 'current' ? 800 : 400}
                  >
                    <Tooltip direction="top" offset={[0, -15]} opacity={0.9}>
                      <span className="text-xs font-semibold">XP {leg.legNumber}b</span>
                    </Tooltip>
                    {renderLegPopup(leg, nextLeg.startLat, nextLeg.startLng,
                      `XP ${leg.legNumber}b — Van Transfer`,
                      nextLeg,
                      false,
                      true
                    )}
                  </Marker>
                </Fragment>
              )}
            </Fragment>
          )
        })}

      {/* proximity circle around end of Leg 36 */}
      {lastLeg && lastLeg.legNumber === 36 && (
        <Circle
          center={[lastLeg.endLat, lastLeg.endLng]}
          radius={SIX_HUNDRED_FEET_METERS}
          color="#f59e0b"
          fillColor="#f59e0b"
          fillOpacity={0.08}
          weight={1.5}
          dashArray="5, 5"
        />
      )}
    </>
  )
})
